import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import InfoAlert from "../../../../common/InfoAlert";
import useInfoAlert from "../../../../../hooks/useInfoAlert";
import { openAuthModal } from "../../../../common/authmodal/authmodalservice";
import { openESignatureConfirmModal } from "../../../../common/esignature/esignatureConfirmService";
import {
  formatReviewReasonLabel,
  renderConditionBadge,
  renderReceiptStatusBadge,
} from "../../epsfBadges";
import { renderAuditLogCards, exportAuditLogToExcel } from "../../epsfAuditDisplay";
import { getStorageTempBadgeTone } from "../../epsfTempCompat";
import { formatStorageTempRange } from "../impProductExcel";
import {
  formatExpiryDisplay,
  sanitizeImpProductLabel,
  trackingLabel,
} from "./inventoryDashboardUtils";
import {
  checkFormReleaseBlockers,
  fetchImpKits,
  fetchReceiptDetail,
  releaseImpReceipt,
} from "../../epsfService";

/** Single review decision — selection is always from held stock. */
const REVIEW_DECISIONS = [
  { value: "Release", label: "Release to Available" },
  { value: "ReturnPending", label: "Return to Sponsor / Depot" },
  { value: "DestructionPending", label: "Send for Destruction" },
];

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function heldQtyFromRow(row) {
  const explicit = row?.HeldQuantity ?? row?.heldQuantity;
  if (explicit != null && explicit !== "") return Math.max(0, toNum(explicit));
  const total = toNum(row?.Quantity ?? row?.quantity);
  const released = toNum(row?.ReleasedQuantity ?? row?.releasedQuantity);
  const ext = toNum(row?.ExternallyReturnedQuantity ?? row?.externallyReturnedQuantity);
  return Math.max(0, total - released - ext);
}

function trackingOf(row) {
  const t = String(
    row?.InventoryTrackingMethod || row?.inventoryTrackingMethod || "",
  ).trim();
  return t === "IndividualKit" ? "IndividualKit" : "BatchQuantity";
}

function selectionLabels(decision) {
  if (decision === "ReturnPending") {
    return {
      kitTitle: "Select held kits",
      kitAria: "Held kits for return",
      qtyTitle: "Quantity from held stock",
      actionVerb: "return pending",
    };
  }
  if (decision === "DestructionPending") {
    return {
      kitTitle: "Select held kits",
      kitAria: "Held kits for destruction",
      qtyTitle: "Quantity from held stock",
      actionVerb: "destruction pending",
    };
  }
  return {
    kitTitle: "Select held kits",
    kitAria: "Held kits to release",
    qtyTitle: "Quantity from held stock",
    actionVerb: "released to Available",
  };
}

function normalizeKitStatus(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
}

/** Kits still awaiting review — selectable. Disposition stages go to Current stage. */
const HOLD_KIT_STATUSES = new Set([
  "requiresreview",
  "hold",
  "quarantined",
]);

function isOnHoldKit(kit) {
  return HOLD_KIT_STATUSES.has(normalizeKitStatus(kit?.Status || kit?.status));
}

/**
 * Review held stock — disposition decision (status only; no ledger qty movement).
 */
export default function ReviewHeldStockModal({
  open,
  row,
  studyId,
  siteId,
  busy = false,
  onClose,
  onConfirm,
}) {
  const isKit = trackingOf(row) === "IndividualKit";
  const heldQty = heldQtyFromRow(row);
  const [decision, setDecision] = useState("Release");
  const [releaseQty, setReleaseQty] = useState(String(heldQty || 0));
  const [kits, setKits] = useState([]);
  const [selectedKitIds, setSelectedKitIds] = useState([]);
  const [kitsBusy, setKitsBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [receiptOverview, setReceiptOverview] = useState(null);
  const [auditRows, setAuditRows] = useState([]);
  const [auditExportBusy, setAuditExportBusy] = useState(false);
  const [releaseBlocked, setReleaseBlocked] = useState(false);
  const [releaseBlockMessage, setReleaseBlockMessage] = useState("");
  const [releaseBlockBusy, setReleaseBlockBusy] = useState(false);
  const [gateReady, setGateReady] = useState(false);
  const { alertState, showAlert, hideAlert } = useInfoAlert();

  const isRelease = decision === "Release";
  const labels = selectionLabels(decision);
  const displayRow = receiptOverview || row || {};

  useEffect(() => {
    if (!open || !row) return;
    setDecision("Release");
    setReleaseQty(String(heldQty || 0));
    setSelectedKitIds([]);
    setReceiptOverview(null);
    setAuditRows([]);
    setReleaseBlocked(false);
    setReleaseBlockMessage("");
    setGateReady(false);
  }, [open, row, heldQty]);

  /* OnReceipt form gate: block Release to Available until form complete/approved. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!open || !row || !studyId || !siteId) {
        setReleaseBlocked(false);
        setReleaseBlockMessage("");
        return;
      }
      const txnId = row.ImpTxnId || row.impTxnId;
      if (!txnId) {
        setReleaseBlocked(false);
        setReleaseBlockMessage("");
        return;
      }
      setReleaseBlockBusy(true);
      try {
        const res = await checkFormReleaseBlockers(studyId, siteId, txnId);
        if (cancelled) return;
        const blockers = Array.isArray(res.rows) ? res.rows : [];
        if (res.success && blockers.length > 0) {
          const titles = blockers
            .map(
              (b) =>
                b.FormTitle ||
                b.formTitle ||
                b.Title ||
                b.title ||
                "Required form",
            )
            .filter(Boolean);
          setReleaseBlocked(true);
          setReleaseBlockMessage(
            titles.length
              ? `Release to Available is blocked until these form(s) are completed and approved: ${titles.join("; ")}.`
              : "Release to Available is blocked until the required receipt form is completed and approved.",
          );
          setDecision("ReturnPending");
        } else {
          setReleaseBlocked(false);
          setReleaseBlockMessage("");
        }
      } catch {
        if (!cancelled) {
          setReleaseBlocked(true);
          setReleaseBlockMessage(
            "Could not verify receipt form requirements. Release to Available is blocked until the check succeeds.",
          );
          setDecision("ReturnPending");
        }
      } finally {
        if (!cancelled) setReleaseBlockBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, row, studyId, siteId]);

  useEffect(() => {
    if (!open) return undefined;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;
    const scrollbarGap =
      window.innerWidth - document.documentElement.clientWidth;
    document.body.classList.add("modal-open");
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    if (scrollbarGap > 0) {
      document.body.style.paddingRight = `${scrollbarGap}px`;
    }
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!open || !row || !studyId || !siteId) {
        setKits([]);
        setAuditRows([]);
        return;
      }
      const txnId = row.ImpTxnId || row.impTxnId;
      if (!txnId) return;

      if (isKit) setKitsBusy(true);

      const detail = await fetchReceiptDetail(studyId, siteId, txnId);
      if (cancelled) return;

      const ds = detail.ds || {};
      const tables = Object.keys(ds)
        .filter((k) => Array.isArray(ds[k]))
        .map((k) => ds[k]);
      const overview = tables[0]?.[0] || detail.rows?.[0] || null;
      if (overview) setReceiptOverview(overview);
      setAuditRows(tables[3] || []);

      if (!isKit) {
        setKits([]);
        setKitsBusy(false);
        return;
      }

      /* Prefer GetReceiptDetail kits — all kits on this receipt (hold + current stage). */
      let nextKits = tables[1] || [];

      if (!nextKits.length) {
        const batchId = row.ImpBatchId || row.impBatchId;
        const list = await fetchImpKits(studyId, siteId, {
          impBatchId: batchId || undefined,
          impProductId: row.ImpProductId || row.impProductId || undefined,
        });
        if (cancelled) return;
        const txnKey = String(txnId).toLowerCase();
        const eligible = (list.rows || []).filter((k) => {
          const receiptId = String(
            k.ReceiptTxnId || k.receiptTxnId || "",
          ).toLowerCase();
          if (txnKey && receiptId === txnKey) return true;
          return !receiptId && isOnHoldKit(k);
        });
        const linked = eligible.filter(
          (k) =>
            String(k.ReceiptTxnId || k.receiptTxnId || "").toLowerCase() ===
            txnKey,
        );
        nextKits = linked.length ? linked : eligible;
      }

      setKits(nextKits);
      const holdIds = nextKits
        .filter(isOnHoldKit)
        .map((k) => String(k.ImpKitId || k.impKitId))
        .filter(Boolean);
      setSelectedKitIds(holdIds);
      setKitsBusy(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, row, isKit, studyId, siteId]);

  const holdKits = useMemo(() => kits.filter(isOnHoldKit), [kits]);
  const stageKits = useMemo(() => kits.filter((k) => !isOnHoldKit(k)), [kits]);

  /* Keep hold kits selected after async loads / auto decision switch. */
  useEffect(() => {
    if (!open || !isKit || kitsBusy) return;
    if (!holdKits.length) return;
    setSelectedKitIds((prev) => {
      if (prev.length) return prev;
      return holdKits
        .map((k) => String(k.ImpKitId || k.impKitId))
        .filter(Boolean);
    });
  }, [open, isKit, kitsBusy, holdKits]);

  /* Avoid ghost-click on Confirm from the same click that opened this modal. */
  useEffect(() => {
    if (!open) {
      setGateReady(false);
      return undefined;
    }
    if (releaseBlockBusy || (isKit && kitsBusy)) {
      setGateReady(false);
      return undefined;
    }
    const timer = window.setTimeout(() => setGateReady(true), 350);
    return () => window.clearTimeout(timer);
  }, [open, releaseBlockBusy, isKit, kitsBusy]);

  const selectedNum = useMemo(() => {
    if (isKit) return selectedKitIds.length;
    if (!isRelease) return heldQty;
    const n = Number(releaseQty);
    return Number.isFinite(n) ? n : NaN;
  }, [isKit, selectedKitIds, isRelease, heldQty, releaseQty]);

  const remainingNum = useMemo(() => {
    if (!Number.isFinite(selectedNum)) return heldQty;
    return Math.max(0, heldQty - selectedNum);
  }, [heldQty, selectedNum]);

  const onDecisionChange = (next) => {
    setDecision(next);
    setReleaseQty(String(heldQty || 0));
    if (isKit && holdKits.length) {
      setSelectedKitIds(
        holdKits.map((k) => String(k.ImpKitId || k.impKitId)).filter(Boolean),
      );
    }
  };

  const toggleKit = (id) => {
    setSelectedKitIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const holdKitIds = useMemo(
    () => holdKits.map((k) => String(k.ImpKitId || k.impKitId)).filter(Boolean),
    [holdKits],
  );
  const allKitsSelected =
    holdKitIds.length > 0 && selectedKitIds.length === holdKitIds.length;
  const someKitsSelected =
    selectedKitIds.length > 0 && selectedKitIds.length < holdKitIds.length;

  const toggleSelectAllKits = () => {
    if (allKitsSelected) setSelectedKitIds([]);
    else setSelectedKitIds(holdKitIds);
  };

  const onExportAuditExcel = async () => {
    if (auditExportBusy || !auditRows.length) return;
    setAuditExportBusy(true);
    try {
      const batchNo = String(
        displayRow.BatchNumber || displayRow.batchNumber || "receipt",
      )
        .replace(/[^\w.-]+/g, "_")
        .slice(0, 40);
      await exportAuditLogToExcel(auditRows, {
        fileName: `ePSF_Receipt_Audit_${batchNo}_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`,
      });
    } catch {
      showAlert({
        title: "Export failed",
        message: "Could not export the audit log to Excel.",
        type: "danger",
        buttonText: "OK",
      });
    } finally {
      setAuditExportBusy(false);
    }
  };

  if (!open || !row) return null;

  const productLabel = sanitizeImpProductLabel(
    displayRow.ProductCode || displayRow.productCode,
    displayRow.ProductName || displayRow.productName,
  );
  const product =
    [productLabel.code, productLabel.name].filter(Boolean).join(" — ") ||
    "Study kit";
  const batch = displayRow.BatchNumber || displayRow.batchNumber || "—";
  const holdReason = formatReviewReasonLabel(
    displayRow.ReviewReason ||
      displayRow.reviewReason ||
      displayRow.StorageOverrideReason ||
      "",
  );
  const status =
    displayRow.ReceiptStatus || displayRow.receiptStatus || "";
  const conditionCode =
    displayRow.ConditionCode || displayRow.conditionCode || "";
  const conditionDetails = String(
    displayRow.ConditionDetails || displayRow.conditionDetails || "",
  ).trim();
  const temp =
    displayRow.TemperatureOnReceipt ?? displayRow.temperatureOnReceipt;
  const tempOut =
    displayRow.TemperatureOutOfRange === true ||
    displayRow.TemperatureOutOfRange === 1 ||
    displayRow.temperatureOutOfRange === true ||
    displayRow.temperatureOutOfRange === 1;
  const storageLabel =
    [
      displayRow.StorageUnitCode || displayRow.storageUnitCode,
      displayRow.StorageUnitName || displayRow.storageUnitName,
    ]
      .filter(Boolean)
      .join(" — ") || "—";
  const rackLabel =
    [
      displayRow.StorageRackCode || displayRow.storageRackCode,
      displayRow.StorageRackName || displayRow.storageRackName,
    ]
      .filter(Boolean)
      .join(" — ") || "";
  const storageTempMin =
    displayRow.StorageMinTempC ??
    displayRow.storageMinTempC ??
    displayRow.MinTempC ??
    displayRow.minTempC;
  const storageTempMax =
    displayRow.StorageMaxTempC ??
    displayRow.storageMaxTempC ??
    displayRow.MaxTempC ??
    displayRow.maxTempC;
  const storageTempUnit =
    displayRow.StorageTempUnit ||
    displayRow.storageTempUnit ||
    displayRow.TempUnit ||
    displayRow.tempUnit ||
    "C";
  const storageTempRange = formatStorageTempRange(
    storageTempMin,
    storageTempMax,
    storageTempUnit,
    "",
  );
  const storageTempTone = getStorageTempBadgeTone(
    storageTempMin,
    storageTempMax,
    storageTempUnit,
  );
  const shipment =
    displayRow.ReferenceNo || displayRow.referenceNo || "—";
  const notes = String(displayRow.Notes || displayRow.notes || "").trim();
  const defaultExpiry = formatExpiryDisplay(
    displayRow.ExpiryDate || displayRow.expiryDate,
  );
  const overrideReason = formatReviewReasonLabel(
    displayRow.StorageOverrideReason || displayRow.storageOverrideReason || "",
  );
  const overrideJust = String(
    displayRow.StorageOverrideJustification ||
      displayRow.storageOverrideJustification ||
      "",
  ).trim();

  const handleConfirm = async () => {
    if (!gateReady || releaseBlockBusy || (isKit && kitsBusy)) return;
    let errorMessage = "";
    if (isRelease && releaseBlocked) {
      errorMessage =
        releaseBlockMessage ||
        "Release to Available is blocked until the required receipt form is completed and approved.";
    } else if (isKit && (!Number.isFinite(selectedNum) || selectedNum <= 0)) {
      errorMessage = "Select at least one held kit for this review decision.";
    } else if (!isKit && (!Number.isFinite(selectedNum) || selectedNum <= 0)) {
      errorMessage = "Enter a held quantity for this review decision.";
    } else if (selectedNum > heldQty) {
      errorMessage = `Selection cannot exceed held quantity (${heldQty}).`;
    } else if (!isRelease && !isKit && selectedNum !== heldQty) {
      errorMessage =
        "Batch quantity return / destruction applies to all held stock.";
    }
    if (errorMessage) {
      showAlert({
        title: "Review held stock",
        message: errorMessage,
        type: "danger",
        buttonText: "OK",
      });
      return;
    }

    if (isRelease) {
      const txnId = row?.ImpTxnId || row?.impTxnId;
      try {
        const res = await checkFormReleaseBlockers(studyId, siteId, txnId);
        const blockers = Array.isArray(res.rows) ? res.rows : [];
        if (res.success && blockers.length > 0) {
          const titles = blockers
            .map((b) => b.FormTitle || b.formTitle || b.Title || b.title)
            .filter(Boolean);
          showAlert({
            title: "Release blocked",
            message: titles.length
              ? `Cannot release: required form(s) incomplete — ${titles.join("; ")}.`
              : "Cannot release: required receipt form is incomplete or awaiting approval.",
            type: "danger",
            buttonText: "OK",
          });
          setReleaseBlocked(true);
          setReleaseBlockMessage(
            titles.length
              ? `Release to Available is blocked until these form(s) are completed and approved: ${titles.join("; ")}.`
              : "Release to Available is blocked until the required receipt form is completed and approved.",
          );
          return;
        }
      } catch {
        showAlert({
          title: "Release blocked",
          message:
            "Could not verify receipt form requirements. Try again after the form is complete.",
          type: "danger",
          buttonText: "OK",
        });
        return;
      }
    }

    const countLabel = isKit
      ? `${selectedNum} kit${selectedNum === 1 ? "" : "s"}`
      : `quantity ${selectedNum}`;
    const actionLabel =
      decision === "ReturnPending"
        ? "mark as return pending"
        : decision === "DestructionPending"
          ? "send for destruction"
          : "release to Available";

    showAlert({
      title: "Confirm review",
      message: `Are you sure you want to ${actionLabel} for ${countLabel}? You must authenticate to complete the final submission.`,
      type: "warning",
      buttonText: "Continue",
      cancelText: "Cancel",
      onConfirm: async () => {
        hideAlert();
        const sign = await openESignatureConfirmModal({
          title: "Sign review",
        });
        if (
          !sign ||
          sign.cancelled ||
          Number(sign.Success ?? sign.success ?? 0) !== 1 ||
          !sign.SignatureBase64
        ) {
          return;
        }

        const auth = await openAuthModal({
          introMessage:
            "Re-authenticate to confirm this held-stock review. Add a review comment.",
          showDescription: true,
          requireDescription: true,
          descriptionLabel: "Review comment",
          descriptionPlaceholder: "Document the review decision…",
          descriptionMaxLength: 1000,
        });
        if (
          !auth ||
          auth.cancelled ||
          Number(auth.Success ?? auth.success ?? 0) !== 1
        ) {
          return;
        }
        const reviewComment = String(
          auth.Description || auth.description || "",
        ).trim();
        if (!reviewComment) return;

        let disposition;
        let releasedQty = 0;
        if (isRelease) {
          disposition = remainingNum > 0 ? "KeepHold" : null;
          releasedQty = selectedNum;
        } else {
          disposition = decision;
          releasedQty = 0;
        }

        const payload = {
          StudyId: studyId,
          SiteId: siteId,
          ImpTxnId: row.ImpTxnId || row.impTxnId,
          ReleaseComment: reviewComment,
          RemainingDisposition: disposition,
        };

        if (isKit) {
          payload.KitIdsJson = JSON.stringify(selectedKitIds);
          payload.Quantity = null;
        } else if (isRelease) {
          payload.Quantity = selectedNum;
          payload.KitIdsJson = null;
        } else {
          payload.Quantity = 0;
          payload.KitIdsJson = null;
        }

        setSubmitting(true);
        try {
          const result = await releaseImpReceipt(payload);
          if (!result.success) {
            showAlert({
              title: "Review failed",
              message: result.message || "Review failed.",
              type: "danger",
              buttonText: "OK",
            });
            return;
          }
          const detail = Array.isArray(result.rows) ? result.rows[0] : null;
          onConfirm?.({
            result,
            detail,
            disposition,
            releasedQty,
            remainingQty: remainingNum,
            row,
          });
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const disabled = busy || submitting || releaseBlockBusy || !gateReady;

  const reviewModal = createPortal(
    <div
      className="modal fade show d-block epsf-modal epsf-review-held-modal"
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-labelledby="epsf-review-held-title"
    >
      <div className="modal-dialog epsf-review-held-modal__dialog">
        <div className="modal-content epsf-review-held-modal__content">
          <div className="modal-header epsf-review-held-modal__header">
            <h5 className="modal-title mb-0" id="epsf-review-held-title">
              Review held stock
            </h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              disabled={disabled}
              onClick={onClose}
            />
          </div>

          <div className="modal-body epsf-review-held-modal__body">
            <div className="epsf-review-held-modal__layout">
              <div className="epsf-review-held-modal__pane epsf-review-held-modal__pane--context">
                <div className="epsf-review-held-modal__pane-head">
                  <h3 className="epsf-review-held-modal__section-label">
                    Receipt details
                  </h3>
                </div>
                <div className="epsf-review-held-modal__panel epsf-review-held-modal__panel--context">
                  <dl className="epsf-review-held-modal__meta">
                    <div>
                      <dt>IMP</dt>
                      <dd>{product}</dd>
                    </div>
                    <div>
                      <dt>Batch / Lot</dt>
                      <dd>
                        <span className="epsf-review-held-modal__batch-with-status">
                          <span>{batch || "—"}</span>
                          {status ? renderReceiptStatusBadge(status) : null}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt>Batch expiry</dt>
                      <dd>{defaultExpiry}</dd>
                    </div>
                    <div>
                      <dt>Tracking</dt>
                      <dd>{trackingLabel(trackingOf(displayRow))}</dd>
                    </div>
                    <div>
                      <dt>Condition</dt>
                      <dd>
                        {conditionCode
                          ? renderConditionBadge(conditionCode)
                          : "—"}
                      </dd>
                    </div>
                    {conditionDetails ? (
                      <div>
                        <dt>Condition details</dt>
                        <dd>{conditionDetails}</dd>
                      </div>
                    ) : null}
                    <div>
                      <dt>Hold reason</dt>
                      <dd>{holdReason || "—"}</dd>
                    </div>
                    <div>
                      <dt>Storage</dt>
                      <dd>
                        <span className="epsf-review-held-modal__storage">
                          <span>{storageLabel}</span>
                          {storageTempRange ? (
                            <span
                              className={`epsf-imp-pill epsf-imp-pill--temp-${storageTempTone}`}
                              title={storageTempRange}
                            >
                              {storageTempRange}
                            </span>
                          ) : null}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt>Storage rack</dt>
                      <dd>{rackLabel || "—"}</dd>
                    </div>
                    <div>
                      <dt>Temp on receipt</dt>
                      <dd>
                        {temp == null || temp === ""
                          ? "—"
                          : `${temp}${tempOut ? " (out of range)" : ""}`}
                      </dd>
                    </div>
                    <div>
                      <dt>Reference</dt>
                      <dd>{shipment}</dd>
                    </div>
                    <div>
                      <dt>{isKit ? "Held kits" : "Held qty"}</dt>
                      <dd>
                        <strong>{heldQty}</strong>
                        {isKit ? (heldQty === 1 ? " kit" : " kits") : null}
                      </dd>
                    </div>
                    {overrideReason ? (
                      <div>
                        <dt>Storage override</dt>
                        <dd>
                          {overrideReason}
                          {overrideJust ? ` — ${overrideJust}` : ""}
                        </dd>
                      </div>
                    ) : null}
                    {notes ? (
                      <div>
                        <dt>Notes</dt>
                        <dd>{notes}</dd>
                      </div>
                    ) : null}
                  </dl>

                  <fieldset
                    className="epsf-review-held-modal__actions"
                    disabled={disabled || releaseBlockBusy}
                  >
                    <legend>Review decision</legend>
                    {releaseBlocked && releaseBlockMessage ? (
                      <div
                        className="epsf-review-held-modal__form-block"
                        role="status"
                      >
                        {releaseBlockMessage}
                      </div>
                    ) : null}
                    {REVIEW_DECISIONS.map((opt) => {
                      const releaseLocked =
                        opt.value === "Release" && releaseBlocked;
                      return (
                        <label
                          key={opt.value}
                          className={`epsf-review-held-modal__radio${
                            releaseLocked
                              ? " epsf-review-held-modal__radio--blocked"
                              : ""
                          }`}
                        >
                          <input
                            type="radio"
                            name="reviewDecision"
                            value={opt.value}
                            checked={decision === opt.value}
                            disabled={releaseLocked || disabled}
                            onChange={() => onDecisionChange(opt.value)}
                          />
                          <span>
                            {opt.label}
                            {releaseLocked ? " (form required)" : ""}
                          </span>
                        </label>
                      );
                    })}
                  </fieldset>
                </div>
              </div>

              <div className="epsf-review-held-modal__pane epsf-review-held-modal__pane--decision">
                <div className="epsf-review-held-modal__pane-head">
                  <h3 className="epsf-review-held-modal__section-label">
                    {isKit ? labels.kitTitle : labels.qtyTitle}
                  </h3>
                </div>
                <div className="epsf-review-held-modal__panel">
                  {isKit ? (
                    <div className="epsf-form-field epsf-review-held-modal__kits-field mb-0">
                      {kitsBusy ? (
                        <div className="form-text">Loading kits…</div>
                      ) : kits.length === 0 ? (
                        <div className="form-text">
                          No kits found for this receipt.
                        </div>
                      ) : (
                        <div
                          className="epsf-review-held-modal__kits"
                          role="group"
                          aria-label={labels.kitAria}
                        >
                          <div
                            className="epsf-review-held-modal__kit-col epsf-review-held-modal__kit-col--hold"
                            role="group"
                            aria-labelledby="epsf-review-kit-hold-title"
                          >
                            <div
                              className="epsf-review-held-modal__kit-title epsf-review-held-modal__kit-title--hold"
                              id="epsf-review-kit-hold-title"
                            >
                              <span>On hold</span>
                              <span className="epsf-review-held-modal__kit-group-count">
                                {holdKits.length}
                              </span>
                            </div>
                            <div className="epsf-review-held-modal__kit-group-body">
                              {holdKits.length > 0 ? (
                                <label className="epsf-review-held-modal__kit epsf-review-held-modal__kit--select-all">
                                  <input
                                    type="checkbox"
                                    checked={allKitsSelected}
                                    ref={(el) => {
                                      if (el)
                                        el.indeterminate = someKitsSelected;
                                    }}
                                    disabled={disabled || !holdKits.length}
                                    onChange={toggleSelectAllKits}
                                  />
                                  <span className="epsf-review-held-modal__kit-main">
                                    <span className="epsf-review-held-modal__kit-no">
                                      Select all
                                    </span>
                                  </span>
                                </label>
                              ) : null}
                              {holdKits.length === 0 ? (
                                <div className="epsf-review-held-modal__kit-group-empty">
                                  No kits on hold.
                                </div>
                              ) : (
                                holdKits.map((k) => {
                                  const id = String(k.ImpKitId || k.impKitId);
                                  const checked = selectedKitIds.includes(id);
                                  const kitExpiry = formatExpiryDisplay(
                                    k.ExpiryDate || k.expiryDate,
                                  );
                                  const kitBatch =
                                    k.BatchNumber || k.batchNumber || batch;
                                  const kitStatus = k.Status || k.status || "";
                                  return (
                                    <label
                                      key={id}
                                      className="epsf-review-held-modal__kit"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={disabled}
                                        onChange={() => toggleKit(id)}
                                      />
                                      <span className="epsf-review-held-modal__kit-main">
                                        <span className="epsf-review-held-modal__kit-no">
                                          {k.KitNumber || k.kitNumber || id}
                                        </span>
                                        <span className="epsf-review-held-modal__kit-meta">
                                          {kitBatch && kitBatch !== "—"
                                            ? kitBatch
                                            : null}
                                          {kitBatch &&
                                          kitBatch !== "—" &&
                                          kitExpiry !== "—"
                                            ? " · "
                                            : null}
                                          {kitExpiry !== "—"
                                            ? `Exp ${kitExpiry}`
                                            : null}
                                        </span>
                                      </span>
                                      <span className="epsf-review-held-modal__kit-status">
                                        {kitStatus
                                          ? renderReceiptStatusBadge(kitStatus)
                                          : null}
                                      </span>
                                    </label>
                                  );
                                })
                              )}
                            </div>
                          </div>

                          <div
                            className="epsf-review-held-modal__kit-col epsf-review-held-modal__kit-col--stage"
                            role="group"
                            aria-labelledby="epsf-review-kit-stage-title"
                          >
                            <div
                              className="epsf-review-held-modal__kit-title epsf-review-held-modal__kit-title--stage"
                              id="epsf-review-kit-stage-title"
                            >
                              <span>Current stage</span>
                              <span className="epsf-review-held-modal__kit-group-count">
                                {stageKits.length}
                              </span>
                            </div>
                            <div className="epsf-review-held-modal__kit-group-body">
                              {stageKits.length === 0 ? (
                                <div className="epsf-review-held-modal__kit-group-empty">
                                  No kits moved to a later stage yet.
                                </div>
                              ) : (
                                stageKits.map((k) => {
                                  const id = String(k.ImpKitId || k.impKitId);
                                  const kitExpiry = formatExpiryDisplay(
                                    k.ExpiryDate || k.expiryDate,
                                  );
                                  const kitBatch =
                                    k.BatchNumber || k.batchNumber || batch;
                                  const kitStatus = k.Status || k.status || "";
                                  return (
                                    <div
                                      key={id}
                                      className="epsf-review-held-modal__kit epsf-review-held-modal__kit--readonly"
                                    >
                                      <span className="epsf-review-held-modal__kit-main">
                                        <span className="epsf-review-held-modal__kit-no">
                                          {k.KitNumber || k.kitNumber || id}
                                        </span>
                                        <span className="epsf-review-held-modal__kit-meta">
                                          {kitBatch && kitBatch !== "—"
                                            ? kitBatch
                                            : null}
                                          {kitBatch &&
                                          kitBatch !== "—" &&
                                          kitExpiry !== "—"
                                            ? " · "
                                            : null}
                                          {kitExpiry !== "—"
                                            ? `Exp ${kitExpiry}`
                                            : null}
                                        </span>
                                      </span>
                                      <span className="epsf-review-held-modal__kit-status">
                                        {kitStatus
                                          ? renderReceiptStatusBadge(kitStatus)
                                          : null}
                                      </span>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : isRelease ? (
                    <div className="epsf-review-held-modal__qty-panel">
                      <div className="epsf-form-field">
                        <label className="form-label">{labels.qtyTitle}</label>
                        <input
                          type="number"
                          className="form-control"
                          min={0}
                          max={heldQty}
                          step="1"
                          disabled={disabled}
                          value={releaseQty}
                          onChange={(e) => setReleaseQty(e.target.value)}
                          aria-label="Quantity from held stock"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="epsf-review-held-modal__qty-panel">
                      <div className="epsf-form-field mb-0">
                        <label className="form-label">{labels.qtyTitle}</label>
                        <input
                          className="form-control"
                          readOnly
                          value={heldQty}
                          aria-label={labels.qtyTitle}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="epsf-review-held-modal__pane epsf-review-held-modal__pane--audit">
                <div className="epsf-review-held-modal__pane-head">
                  <h3 className="epsf-review-held-modal__section-label">
                    Audit log
                  </h3>
                  <div className="epsf-review-held-modal__kit-tools">
                    <button
                      type="button"
                      className="btn btn-link"
                      disabled={
                        disabled || auditExportBusy || !auditRows.length
                      }
                      onClick={onExportAuditExcel}
                      title="Export audit log to Excel"
                    >
                      <i className="fas fa-file-excel me-1" aria-hidden="true" />
                      {auditExportBusy ? "Exporting…" : "Export Excel"}
                    </button>
                  </div>
                </div>
                <div
                  className="epsf-review-held-modal__panel epsf-review-held-modal__panel--audit"
                  aria-label="Receipt audit log"
                >
                  <div
                    className="epsf-storage-detail-list"
                    role="list"
                    aria-label="Audit log"
                  >
                    {renderAuditLogCards(auditRows, {
                      emptyMessage: "No audit records for this receipt yet.",
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer epsf-review-held-modal__footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              disabled={disabled}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={disabled}
              onClick={handleConfirm}
            >
              {submitting ? "Saving…" : "Confirm review"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );

  return (
    <>
      {reviewModal}
      <InfoAlert {...alertState} onClose={hideAlert} />
    </>
  );
}
